// ====================================================================================================
//  File Name   : SwarmConstellationGraph.js
//  Location    : src/features/swarm/SwarmConstellationGraph.js
//  Project     : AIRS Security - Autonomous Sentinel Ecosystem
//  Developed By: Appence Software Solution | https://appence.com
//  Legal Notice: Proprietary Property. Unauthorized use strictly prohibited. (c) 2026
// ====================================================================================================

import React, { useRef, useEffect, useState } from 'react';

const SwarmConstellationGraph = ({ activeAgents = [], recentMessages = [], onSelectAgent }) => {
  const canvasRef = useRef(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let angleOffset = 0;
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = canvas.parentElement.offsetWidth || 800;
      canvas.height = 360;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle pulse emitter for inter-agent communication
    for (let i = 0; i < 8; i++) {
      particles.push({
        progress: Math.random(),
        speed: 0.008 + Math.random() * 0.012,
        agentIdx: i
      });
    }

    const render = () => {
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      angleOffset += 0.005;

      ctx.clearRect(0, 0, width, height);

      // 1. Tactical Circular Radar Grids
      ctx.strokeStyle = 'rgba(6, 182, 212, 0.08)';
      ctx.lineWidth = 1;
      [70, 130, 160].forEach(r => {
        ctx.beginPath();
        ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
        ctx.stroke();
      });

      // 2. Passive Sweeping Sonar Wave
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(angleOffset * 1.5);
      const sweepGrad = ctx.createLinearGradient(0, 0, 160, 0);
      sweepGrad.addColorStop(0, 'rgba(6, 182, 212, 0.15)');
      sweepGrad.addColorStop(1, 'rgba(6, 182, 212, 0)');
      ctx.fillStyle = sweepGrad;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, 160, -0.3, 0.3);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // 3. Central Core: AIRSX-72B Supreme Commander
      const corePulse = Math.sin(angleOffset * 4) * 3;
      const coreGrad = ctx.createRadialGradient(centerX, centerY, 5, centerX, centerY, 32 + corePulse);
      coreGrad.addColorStop(0, 'rgba(168, 85, 247, 1)');
      coreGrad.addColorStop(0.5, 'rgba(6, 182, 212, 0.8)');
      coreGrad.addColorStop(1, 'rgba(6, 182, 212, 0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 32 + corePulse, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(centerX, centerY, 8, 0, Math.PI * 2);
      ctx.fill();

      ctx.font = '10px monospace';
      ctx.fillStyle = '#c084fc';
      ctx.textAlign = 'center';
      ctx.fillText('AIRSX-72B CORE', centerX, centerY + 24);

      // 4. Render Orbital Micro-Sentinels
      const agentCount = activeAgents.length;
      const orbitRadius = 130;
      const currentNodes = [];

      activeAgents.forEach((agent, idx) => {
        const theta = (idx / Math.max(1, agentCount)) * (Math.PI * 2) + angleOffset * 0.4;
        const x = centerX + Math.cos(theta) * orbitRadius;
        const y = centerY + Math.sin(theta) * orbitRadius;

        currentNodes.push({ ...agent, x, y });

        // Synaptic Thread from Core to Micro-Agent
        ctx.strokeStyle = 'rgba(16, 185, 129, 0.25)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.stroke();

        // Traveling Energy Photons along filament
        particles.forEach(p => {
          if (p.agentIdx % Math.max(1, agentCount) === idx) {
            p.progress = (p.progress + p.speed) % 1;
            const px = centerX + (x - centerX) * p.progress;
            const py = centerY + (y - centerY) * p.progress;
            ctx.fillStyle = '#34d399';
            ctx.beginPath();
            ctx.arc(px, py, 2.5, 0, Math.PI * 2);
            ctx.fill();
          }
        });

        // Inter-Agent peer mesh connection
        if (agentCount > 1) {
          const nextIdx = (idx + 1) % agentCount;
          const nextTheta = (nextIdx / agentCount) * (Math.PI * 2) + angleOffset * 0.4;
          const nx = centerX + Math.cos(nextTheta) * orbitRadius;
          const ny = centerY + Math.sin(nextTheta) * orbitRadius;

          ctx.strokeStyle = 'rgba(6, 182, 212, 0.15)';
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(nx, ny);
          ctx.stroke();
        }

        // Micro-Sentinel Node Halo
        const isHovered = hoveredNode && hoveredNode.agent_uuid === agent.agent_uuid;
        const haloR = isHovered ? 14 : 9;
        const haloGrad = ctx.createRadialGradient(x, y, 2, x, y, haloR + 6);
        haloGrad.addColorStop(0, '#10b981');
        haloGrad.addColorStop(1, 'rgba(16, 185, 129, 0)');
        ctx.fillStyle = haloGrad;
        ctx.beginPath();
        ctx.arc(x, y, haloR + 6, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = isHovered ? '#34d399' : '#059669';
        ctx.beginPath();
        ctx.arc(x, y, haloR, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();

        // Node Label
        ctx.font = '9px monospace';
        ctx.fillStyle = '#6ee7b7';
        ctx.textAlign = 'center';
        ctx.fillText(agent.agent_type || 'SENTINEL', x, y - 14);
      });

      // Canvas Pointer Detection
      canvas.onmousemove = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const found = currentNodes.find(n => Math.hypot(n.x - mx, n.y - my) <= 16);
        setHoveredNode(found || null);
        canvas.style.cursor = found ? 'pointer' : 'default';
      };

      canvas.onclick = () => {
        if (hoveredNode && onSelectAgent) {
          onSelectAgent(hoveredNode);
        }
      };

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [activeAgents, hoveredNode, onSelectAgent]);

  return (
    <div className="relative w-full bg-[#030712] border border-cyan-950/80 rounded-xl overflow-hidden shadow-2xl">
      {/* Header Overlay */}
      <div className="absolute top-3 left-4 z-10 flex items-center gap-2 pointer-events-none">
        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping"></span>
        <span className="font-mono text-xs font-bold text-cyan-300 tracking-wider">
          NEURAL CONSTELLATION MESH (60 FPS)
        </span>
        <span className="text-[10px] text-slate-500 font-mono">
          | Orbiting Sentinels: {activeAgents.length}
        </span>
      </div>

      {/* HTML5 Canvas */}
      <canvas ref={canvasRef} className="w-full block" />

      {/* Hover Card Overlay */}
      {hoveredNode && (
        <div className="absolute bottom-3 left-4 z-10 bg-slate-950/95 border border-emerald-500/60 p-2.5 rounded-lg font-mono text-[11px] text-slate-200 shadow-xl pointer-events-none max-w-sm">
          <div className="font-bold text-emerald-400">🛡️ {hoveredNode.agent_name}</div>
          <div className="text-[10px] text-slate-400">Scope: {hoveredNode.target_scope}</div>
          <div className="text-[10px] text-cyan-400 truncate">Objective: {hoveredNode.objective}</div>
        </div>
      )}
    </div>
  );
};

export default SwarmConstellationGraph;
